import React, { useRef, useState } from 'react';
import ReactSignatureCanvas from 'react-signature-canvas';
import './SignaturePad.css';

const TABS = { DRAW: 'draw', UPLOAD: 'upload' };

const SignaturePad = ({ value, onChange, label = 'Signature' }) => {
    const canvasRef  = useRef(null);
    const fileRef    = useRef(null);
    const [open, setOpen]       = useState(false);
    const [tab, setTab]         = useState(TABS.DRAW);
    const [preview, setPreview] = useState(''); // upload preview before saving
    const [hasSigned, setHasSigned] = useState(false);

    const openPad = () => {
        setTab(TABS.DRAW);
        setPreview('');
        setHasSigned(false);
        setOpen(true);
    };

    const cancel = () => {
        setPreview('');
        setOpen(false);
    };

    // ── Draw tab ──────────────────────────────────────────
    const clearCanvas = () => {
        canvasRef.current?.clear();
        setHasSigned(false);
    };

    const saveDraw = () => {
        if (!hasSigned || !canvasRef.current) return;
        try {
            onChange(canvasRef.current.getTrimmedCanvas().toDataURL('image/png'));
        } catch {
            onChange(canvasRef.current.toDataURL('image/png'));
        }
        setOpen(false);
    };

    // ── Upload tab ────────────────────────────────────────
    const handleFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => setPreview(ev.target.result);
        reader.readAsDataURL(file);
        // reset input so the same file can be re-selected if needed
        e.target.value = '';
    };

    const saveUpload = () => {
        if (!preview) return;
        onChange(preview);
        setPreview('');
        setOpen(false);
    };

    const erase = () => onChange('');

    return (
        <div className="sig-wrapper">
            {value ? (
                <div className="sig-preview-wrap">
                    <img src={value} alt={label} className="sig-preview-img" />
                    <button type="button" className="sig-btn sig-btn-sm no-print" onClick={openPad}>Re-sign</button>
                    <button type="button" className="sig-btn sig-btn-sm sig-btn-danger no-print" onClick={erase}>Clear</button>
                </div>
            ) : (
                <button type="button" className="sig-btn no-print" onClick={openPad}>+ Add Signature</button>
            )}

            {open && (
                <div className="sig-modal-backdrop no-print" onClick={cancel}>
                    <div className="sig-modal" onClick={e => e.stopPropagation()}>

                        {/* header */}
                        <div className="sig-modal-header">
                            <span>{label}</span>
                            <button type="button" className="sig-close" onClick={cancel}>✕</button>
                        </div>

                        {/* tab switcher */}
                        <div className="sig-tabs">
                            <button
                                type="button"
                                className={`sig-tab ${tab === TABS.DRAW ? 'sig-tab-active' : ''}`}
                                onClick={() => { setTab(TABS.DRAW); setPreview(''); }}
                            >
                                Draw
                            </button>
                            <button
                                type="button"
                                className={`sig-tab ${tab === TABS.UPLOAD ? 'sig-tab-active' : ''}`}
                                onClick={() => setTab(TABS.UPLOAD)}
                            >
                                Upload
                            </button>
                        </div>

                        {/* ── Draw panel ── */}
                        {tab === TABS.DRAW && (
                            <>
                                <div className="sig-canvas-wrap">
                                    <ReactSignatureCanvas
                                        ref={canvasRef}
                                        penColor="black"
                                        canvasProps={{ className: 'sig-canvas' }}
                                        onEnd={() => setHasSigned(true)}
                                    />
                                </div>
                                <div className="sig-modal-footer">
                                    <button type="button" className="sig-btn sig-btn-outline" onClick={clearCanvas}>Clear</button>
                                    <button type="button" className="sig-btn sig-btn-primary" onClick={saveDraw}>Save</button>
                                </div>
                            </>
                        )}

                        {/* ── Upload panel ── */}
                        {tab === TABS.UPLOAD && (
                            <>
                                <div className="sig-upload-panel">
                                    {preview ? (
                                        <div className="sig-upload-preview">
                                            <img src={preview} alt="signature preview" className="sig-upload-img" />
                                            <button
                                                type="button"
                                                className="sig-btn sig-btn-sm sig-btn-outline"
                                                onClick={() => { setPreview(''); fileRef.current.click(); }}
                                            >
                                                Change
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            className="sig-upload-trigger"
                                            onClick={() => fileRef.current.click()}
                                        >
                                            <span className="sig-upload-icon">↑</span>
                                            <span>Click to upload image</span>
                                            <span className="sig-upload-hint">PNG, JPG, GIF supported</span>
                                        </button>
                                    )}
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={handleFile}
                                    />
                                </div>
                                <div className="sig-modal-footer">
                                    <button type="button" className="sig-btn sig-btn-outline" onClick={cancel}>Cancel</button>
                                    <button type="button" className="sig-btn sig-btn-primary" onClick={saveUpload} disabled={!preview}>Save</button>
                                </div>
                            </>
                        )}

                    </div>
                </div>
            )}
        </div>
    );
};

export default SignaturePad;
